// src/pages/personal/analytics/CustomerSection/components/LeadSourceDistribution.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/Layout';
import { COLORS } from '@/theme';
import { DataList, DataListColumnDef } from '@/components/ui/DataList';
import { LeadSourceDistributionChart } from './LeadSourceDistributionChart';
// Импортируем форматтеры
import { formatPercent, formatNumber } from '@/utils/Formatters';

import styles from '../CustomerSection.module.css';

interface StatusItem {
  status: string;
  name: string;
  count: number;
  percentage: number;
  change: number;
  trend: 'up' | 'down' | 'neutral';
}

interface LeadSourceDistributionProps {
  data: StatusItem[];
  isLoading?: boolean;
}

// Тип для ключей в clientChartColors объекте
type ClientChartColorKey = keyof typeof COLORS.clientChartColors;

const LeadSourceDistribution: React.FC<LeadSourceDistributionProps> = ({ data, isLoading = false }) => {
  const { t, i18n } = useTranslation('customer');
  const currentLocale = i18n.language;
  
  // Функция для получения цвета статуса из темы
  const getStatusColor = (status: string): string => {
    const statusKey = status.toLowerCase();
    return statusKey in COLORS.clientChartColors
      ? COLORS.clientChartColors[statusKey as ClientChartColorKey]
      : COLORS.clientChartColors.default;
  };
  
  // Подготовка данных для диаграммы
  const chartData = data.map(item => ({
    name: t(`statuses.${item.status}`, { defaultValue: item.name }),
    value: item.count,
    percentage: item.percentage,
    color: getStatusColor(item.status)
  }));
  
  // Определение колонок для списка
  const columns: DataListColumnDef<StatusItem>[] = [
    {
      key: 'status',
      title: t('status_distribution.status'),
      width: 'lg',
      render: (item: StatusItem) => (
        t(`statuses.${item.status}`, { defaultValue: item.name })
      )
    },
    {
      key: 'count',
      title: t('status_distribution.clients'),
      width: 'sm',
      textAlign: 'right',
      render: (item: StatusItem) => formatNumber(item.count, 0, true, currentLocale)
    },
    {
      key: 'percentage',
      title: t('status_distribution.percentage'),
      width: 'sm',
      textAlign: 'right',
      render: (item: StatusItem) => formatPercent(item.percentage, 1, true, currentLocale)
    },
    {
      key: 'change',
      title: t('status_distribution.change'),
      width: 'sm',
      textAlign: 'right',
      render: (item: StatusItem) => {
        const trendIcon = item.trend === 'up' 
          ? '↑' 
          : item.trend === 'down' 
            ? '↓' 
            : '−';
        
        const trendClass = item.trend === 'up'
          ? 'text-green-600'
          : item.trend === 'down'
            ? 'text-red-600'
            : 'text-gray-500';
            
        const formattedChange = formatPercent(Math.abs(item.change), 1, false, currentLocale);
            
        return (
          <span className={trendClass}>
            {trendIcon} {formattedChange}%
          </span>
        );
      }
    }
  ];
  
  // Общий компонент для отображения состояния загрузки или отсутствия данных
  const renderLoader = () => (
    <div className={styles.loadingContainer}>
      <div className={styles.loadingSpinner}></div>
      <p className={styles.loadingText}>{t('loading')}</p>
    </div>
  );
  
  // Компонент для отображения отсутствия данных
  const renderNoData = () => (
    <div className={styles.noDataContainer}>
      <p>{t('no_data')}</p>
    </div>
  );
  
  // Рендер контента в зависимости от состояния
  const renderContent = () => {
    if (isLoading) {
      return renderLoader();
    }
    
    if (!data || data.length === 0) {
      return renderNoData();
    }
    
    return (
      <div className={styles.distributionContainer}>
        <div className={styles.chartSide}>
          <LeadSourceDistributionChart data={chartData} />
        </div>
        
        <div className={styles.dataSide}>
          <DataList 
            data={data}
            columns={columns}
            noDataText={t('no_data')}
            compact={true}
            getItemColor={(item) => getStatusColor(item.status)}
          />
        </div>
      </div>
    );
  };
  
  return (
    <Card padding="none" shadow="sm" className="w-full">
      <Card.Header className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{t('status_distribution.title')}</h3>
      </Card.Header>
      <Card.Body className={styles.cardContent}>
        {renderContent()}
      </Card.Body>
    </Card>
  );
};

export default LeadSourceDistribution;